import React, { FC } from 'react'
import {
  Anchor,
  Button,
  Flex,
  LoadingSpinner,
  Spacer,
  Text,
} from '@sendoutcards/quantum-design-ui'
import {
  SelectedItemCard,
  SelectedItemCardProps,
} from '../shared/SelectedItemCard'
import { TotalsTable } from './TotalsTable'
import { PaymentMethod } from 'src/upsale/components/checkout/payment/PaymentMethod'
import { Icon } from 'src/design_system/atoms/icons/components/Icon'
import {
  useAccount,
  useActions,
  useEffect,
  useMutations,
  useState,
} from 'src/hooks'
import { PaymentMethodForm } from './payment/PaymentMethodForm'
import { PurchaseIntent } from 'src/graphql/generated/graphql'
import { AccountInput, PlanFragment } from 'src/legacy_graphql'
import { parseError } from 'src/utils/parseError'
import { PlanDiffDialog } from 'src/pricing_page/components/planDiff/PlanDiffDialog'
import { useGetPlanMove } from 'src/react-query/queries/hooks'
import { ProcessingPurchase } from './ProcessingPurchase'
import { useForceAccountRefresh } from 'src/hooks/useForceAccountRefresh'

export type UpsaleCheckoutProps = {
  title: string
  description: string
  selectedItem: PlanFragment
  actionTitle: string
  onPurchaseSuccess: (successMsg: string) => void
  onCancel: () => void
}

export const UpsaleCheckout: FC<UpsaleCheckoutProps> = ({
  title,
  description,
  selectedItem,
  actionTitle,
  onPurchaseSuccess,
  onCancel,
}) => {
  const [
    shouldShowEditPaymentMethod,
    setShouldShowEditPaymentMethod,
  ] = useState(false)
  const account = useAccount()
  const mutations = useMutations()
  const actions = useActions()
  const paymentMethod = account.stripeSource

  const forceAccountRefresh = useForceAccountRefresh()

  // For some odd reason, the query keeps firing after some actions (like switch tabs, hiting the tab key, etc)
  // For now I will add a small blocker so this doesnt break the user flow but we need to revisit this soon
  const { isLoading: isPlanMoveIsLoading, data: planMoveData } = useGetPlanMove(
    {
      planIdFrom: Number(account.plan.id),
      planIdTo: Number(selectedItem.id),
    },
  )
  const [shouldShowLoad, setShouldShowLoad] = useState(true)

  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const [isUpdatingAccount, setIsUpdatingAccount] = useState(false)
  const [isCommitingPurchase, setIsCommitingPurchase] = useState(false)
  const [isLoadingPIntent, setIsLoadingPIntent] = useState(false)
  const [pIntent, setPItent] = useState<PurchaseIntent | undefined>()

  const isPaymentDisabled =
    isLoadingPIntent ||
    isCommitingPurchase ||
    !paymentMethod ||
    (selectedItem.isAddon && !pIntent && selectedItem.id !== '122') ||
    isUpdatingAccount

  const handlePurchase = (isDowngrade: boolean) => {
    if (!selectedItem.isAddon || selectedItem.id === '122')
      handlePurchasePlan(isDowngrade)
    else handlePurchaseProduct(isDowngrade)
  }

  const updateAccount = async (
    account: AccountInput,
    isDowngrade: boolean,
    error: (error: Error | string) => void = () => {},
  ) => {
    try {
      const {
        updateAccount: { account: updatedAccount },
      } = await mutations.updateAccount({ account })
      actions.updatedAccount(updatedAccount)
      // It may take some time for the webhook to run
      setTimeout(() => forceAccountRefresh(), 5_000)
      setTimeout(() => forceAccountRefresh(), 15_000)
      handlePurchaseSuccess(isDowngrade)
    } catch (err) {
      error(parseError(err))
      try {
        setErrorMessage(
          err instanceof Error && err.message.split(': ')[1] !== undefined
            ? err.message.split(': ')[1]
            : 'Account failed to update. Contact Support for assistance.',
        )
      } catch {
        setErrorMessage(
          'Account failed to update. Contact Support for assistance.',
        )
      }
    } finally {
      setIsUpdatingAccount(false)
    }
  }

  const handlePurchasePlan = (isDowngrade: boolean) => {
    const accountData: AccountInput = {
      ...(selectedItem.isAddon
        ? { planAddon: selectedItem.id }
        : { plan: selectedItem.id }),
    }

    setIsUpdatingAccount(true)

    updateAccount(accountData, isDowngrade)
  }

  // Note: Wont be able to use this for now as we don't have !isAddon.
  const handlePurchaseProduct = async (isDowngrade: boolean) => {
    if (pIntent) {
      setIsCommitingPurchase(true)
      try {
        const { commitPurchase } = await mutations.commitPurchase({
          purchaseIntentId: pIntent.id,
          doNotShowDefaultTransition: true,
        })

        // TODO: How should we update cache with the paid info?
        console.log('paid: ', commitPurchase.paid)
        handlePurchaseSuccess(isDowngrade)
      } catch (error) {
        console.log(error)
      } finally {
        setIsCommitingPurchase(false)
      }
    }
  }

  const handlePurchaseSuccess = (isDowngrade: boolean) => {
    const successMsg = isDowngrade
      ? 'You have successfully downgraded to'
      : 'Way to go! You have successfully upgraded to'

    onPurchaseSuccess(successMsg)
  }

  useEffect(() => {
    const createPurchaseIntent = async () => {
      try {
        setIsLoadingPIntent(true)
        const {
          createPurchase: { purchaseIntent },
        } = await mutations.createPurchase({
          itemsToPurchase: [{ itemCode: selectedItem.stripeId, quantity: 1 }],
          doNotShowDefaultTransition: true,
        })
        setPItent(purchaseIntent)
      } catch (error) {
        console.log(error)
      } finally {
        setIsLoadingPIntent(false)
      }
    }
    if (selectedItem.isAddon && selectedItem.id !== '122')
      createPurchaseIntent()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!isPlanMoveIsLoading && shouldShowLoad) {
      setShouldShowLoad(false)
    }
  }, [isPlanMoveIsLoading, shouldShowLoad])

  const currentPlanCardProps: SelectedItemCardProps = {
    id: account.plan.id,
    title: account.plan.title,
    price: account.plan.price.description,
    backgroundColor: '#4ab4d8',
    icon: 'smile_card_w_heart',
    recurrence: account.plan.isAddon ? 'pkg' : 'mo',
  }

  const newPlanCardProps: SelectedItemCardProps = {
    id: selectedItem.id,
    title: selectedItem.title,
    price: selectedItem.price.description,
    backgroundColor: '#D66DE0',
    icon: 'smile_card_w_heart',
    recurrence: selectedItem.isAddon ? 'pkg' : 'mo',
  }

  return (
    <>
      {shouldShowLoad && account.plan.id !== '1' ? (
        <Flex
          minWidth="300px"
          minHeight="300px"
          width="100%"
          height="calc(100% - 70px)"
          justifyContent="center"
          alignItems="center"
        >
          <LoadingSpinner size="xLarge" />
        </Flex>
      ) : !selectedItem.isAddon &&
        planMoveData &&
        !planMoveData.planMove.isImmediate &&
        selectedItem.id !== '122' ? (
        <>
          <PlanDiffDialog
            title="You’ve chosen to change your subscription."
            description={
              planMoveData.planMove.type === 'DOWN'
                ? 'If you’re sure you want to downgrade, that’s okay! There are many great ways to share heartfelt kindness. Getting extra benefits and discounts on top of your promptings are just better! Click below to confirm the changes to your subscription and benefits.'
                : 'Review the information below and confirm your upgrade.'
            }
            currentPlan={currentPlanCardProps}
            newPlan={newPlanCardProps}
            action={{
              title: 'Yes, I confirm',
              onClick: () => {
                handlePurchase(planMoveData.planMove.type === 'DOWN')
              },
            }}
            onClose={onCancel}
          />
          {isUpdatingAccount && <ProcessingPurchase />}
        </>
      ) : (
        <Flex width="100%" flexDirection="column" inset="x2">
          <Flex
            position={shouldShowEditPaymentMethod ? 'absolute' : 'relative'}
            width="100%"
            flexDirection="column"
            left={shouldShowEditPaymentMethod ? '0' : undefined}
            bottom={shouldShowEditPaymentMethod ? '0' : undefined}
            top={shouldShowEditPaymentMethod ? '0' : undefined}
            overflow={shouldShowEditPaymentMethod ? 'hidden' : undefined}
          >
            <Flex flexDirection="column" justifyContent="flex-start">
              <Text
                content={title}
                type="largeBody"
                weight="extraBold"
                alignment="left"
                color="primaryHeading"
              />
              <Text content={description} type="body" alignment="left" />
            </Flex>
            <Spacer space="x4" />
            <Flex flexDirection="column">
              <Text
                content="Selected Product"
                type="largeBody"
                weight="bold"
                color="primaryHeading"
              />
              <Spacer space="x_75" />
              <SelectedItemCard
                id={selectedItem.id}
                icon="smile_card_w_heart"
                backgroundColor="#4AB4D8"
                title={selectedItem.title}
                price={selectedItem.price.description}
                recurrence={selectedItem.isAddon ? undefined : 'mo'}
              />
            </Flex>
            {/* Payment */}
            <Spacer space="x5" />
            <Flex flexDirection="row" alignItems="center">
              <Text
                content="Payment Method"
                type="largeBody"
                weight="bold"
                color="primaryHeading"
              />
              <Flex
                alignItems="center"
                style={{ marginLeft: 'auto', marginRight: '8px' }}
                onClick={() => setShouldShowEditPaymentMethod(true)}
                cursor="pointer"
              >
                <Icon name="edit" size={16} />
                <Spacer space="x1" orientation="horizontal" />
                <Text
                  type="caption"
                  weight="bold"
                  color="primaryHeading"
                  content="Edit"
                  style={{ textDecoration: 'underline' }}
                />
              </Flex>
            </Flex>
            <Flex flexDirection="column">
              <Spacer space="x2_5" />
              <PaymentMethod
                cardHolderName={`${account.firstName} ${account.lastName}`}
                paymentMethod={paymentMethod ?? undefined}
                handlePaymentMethod={() =>
                  setShouldShowEditPaymentMethod(!shouldShowEditPaymentMethod)
                }
              />
            </Flex>
            <Spacer space="x4" />
            <Flex flexDirection="column">
              {pIntent && (
                <>
                  <TotalsTable
                    subtotal={pIntent.subtotal}
                    taxTotal={pIntent.tax}
                    orderTotal={pIntent.total}
                  />
                  <Spacer space="x1" />
                </>
              )}
            </Flex>
            {errorMessage && (
              <Flex width="100%" alignItems="center" flexDirection="column">
                <Text type="largeBody" weight="bold" color="danger">
                  An error occurred
                </Text>
                <Spacer space="x1" />
                <Text type="body">{errorMessage}</Text>
                <Spacer space="x2_5" />
                <Button
                  type="secondary"
                  title="Dismiss"
                  onClick={() => setErrorMessage(undefined)}
                />
                <Spacer space="x3" />
              </Flex>
            )}
            <Flex width="100%" justifyContent={'end'} alignItems="center">
              <Flex alignItems="center">
                <Anchor title="cancel" isDecorated={true} onClick={onCancel} />
                <Spacer space="x4" orientation="horizontal" />
                <Button
                  title={actionTitle}
                  onClick={() => handlePurchase(false)}
                  backgroundColorOverride="#404040"
                  disabled={isPaymentDisabled}
                />
              </Flex>
            </Flex>
          </Flex>
          {isUpdatingAccount && <ProcessingPurchase />}
          {shouldShowEditPaymentMethod && (
            <PaymentMethodForm
              title="Add/Edit Payment"
              onDismissModal={() => {
                setShouldShowEditPaymentMethod(false)
              }}
            />
          )}
        </Flex>
      )}
    </>
  )
}
